import React, { useState } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import DesignOption from '@design-system/option';
import TextButton from '@design-system/text-button';
import Flag from '@component/common/flag';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useChangeStudyLanguage, useStudyConfiguration } from '@hook/iyagigo/useStudent';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { IYAGIGO_STUDY_LANGUAGE_OPTIONS } from '@constant/iyagigo/variable';

// ###################################
// StudyLanguageModal — 학습언어 선택 (홈 상단 칩에서 진입)
// - 학습언어를 바꾸면 사이드탭 메뉴 구성 전체가 갈리므로, 즉시 토글하지 않고 '저장하기'로 확정
// - 옵션 클릭 → 로컬 선택(하이라이트)만, 하단 '저장하기' 버튼으로 확정(전환 + 닫기)
// - 현재 언어를 그대로 저장하면 서버 호출 없이 닫기만 한다
// - DesignOption은 label을 문자열로만 받아 국기를 넣을 수 없어, 국기는 옵션 위에 겹쳐 배치
// ###################################

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const OptionItemContainer = styled.div`
  position: relative;
  width: 100%;
`;

const FlagContainer = styled.div`
  position: absolute;
  top: 50%;
  left: ${size.MARGIN_1};
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
`;

const StudyLanguageModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const studyConfiguration = useStudyConfiguration();
  const changeStudyLanguage = useChangeStudyLanguage();
  const currentStudyLanguage = studyConfiguration?.studyLanguage ?? EApiLanguageCode.JAPANESE;
  const [selectedStudyLanguage, setSelectedStudyLanguage] = useState<EApiLanguageCode>(currentStudyLanguage);

  const handleSave = () => {
    if (selectedStudyLanguage !== currentStudyLanguage) changeStudyLanguage(selectedStudyLanguage);
    dispatch(modalActions.hideModal());
  };

  const Options = IYAGIGO_STUDY_LANGUAGE_OPTIONS.map((option) => (
    <OptionItemContainer key={option.value}>
      <FlagContainer>
        <Flag language={option.value} size='large' />
      </FlagContainer>
      <DesignOption
        theme={ETheme.BROWN}
        align='center'
        width='100%'
        label={option.text}
        value={option.description}
        selected={option.value === selectedStudyLanguage}
        handleClick={() => setSelectedStudyLanguage(option.value)}
      />
    </OptionItemContainer>
  ));

  return (
    <Wrapper>
      <TextBlock
        text='어떤 언어를 공부할까요?'
        design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'center' } }}
      />
      <Gap height={size.MARGIN_2} />
      <OptionContainer>{Options}</OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='시작하기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        handleClick={handleSave}
      />
    </Wrapper>
  );
};

export default StudyLanguageModal;
